export default class Tabs {
    constructor($el, options = {}) {
        this.DOM = { $el }
        this.options = { ...Tabs.prototype.defaults, ...options }
        this.init()
    }

    init() {
        this.DOM.$tabsContainer = this.DOM.$el.querySelector(':scope > .c-tabs__container')
        this.DOM.$tabsList = this.DOM.$tabsContainer.querySelector(':scope > .c-tabs__list')
        this.DOM.$tabs = Array.prototype.slice.call(this.DOM.$tabsList.querySelectorAll('[role="tab"]'))
        this.DOM.tabPanelIds = this.DOM.$tabs.reduce((tabPanelIds, $tab) => tabPanelIds = [...tabPanelIds, ...$tab.getAttribute('aria-controls').split(',')], [])
        this.DOM.$tabPanels = this.DOM.tabPanelIds.map(tabPanelId => document.getElementById(tabPanelId))
        this.DOM.$navItems = this.DOM.$tabsContainer.querySelectorAll(':scope > .c-tabs__scroller')

        this.DOM.$tabs.forEach($tab => {
            $tab.addEventListener('click', this.onTabClick.bind(this))
        })

        window.addEventListener('popstate', this.onPopstate.bind(this))

        this.DOM.$el.addEventListener('element:should-be-visible', this.onElementShouldBeVisible.bind(this))

        if (document.location.hash) {
            const hash = document.location.hash.replace('#', '')
            const regExp = new RegExp(`^${this.options.hashPrefix}`)

            if (hash.match(regExp)) {
                this.onPopstate({
                    fromInit: true,
                })
            }
        }

        new ResizeObserver(() => this.toggleHorizontallyScrollable()).observe(this.DOM.$el)
        window.addEventListener('resize', this.toggleHorizontallyScrollable.bind(this))

        this.DOM.$navItems.forEach($navItem => $navItem.addEventListener('click', this.scrollHorizontally.bind(this)))
    }

    onTabClick(e) {
        if (e.currentTarget.getAttribute('aria-selected') === 'true') {
            return true
        }

        e.preventDefault()

        const $activeTab = e.currentTarget.getAttribute('aria-controls').split(',').reduce(
            ($activeTab, tabPanelId) => {
                if ($activeTab) {
                    return $activeTab
                }

                const $tabPanel = document.getElementById(tabPanelId)

                if ($tabPanel) {
                    return $tabPanel.querySelector('[role="tab"][aria-selected="true"]')
                }
            },
            null
        )

        if ($activeTab) {
            document.location.hash = `#${this.options.hashPrefix}${$activeTab.id}`
        } else {
            document.location.hash = `#${this.options.hashPrefix}${e.currentTarget.id}`
        }
    }

    onPopstate(e) {
        const hash = document.location.hash.replace('#', '')
        const regExp = new RegExp(`^${this.options.hashPrefix}`)

        if (!hash.match(regExp)) {
            return true
        }

        const currentTabId = hash.substring(this.options.hashPrefix.length)
        const $activeTab = this.DOM.$tabsList.querySelector('[id="' + currentTabId + '"]')

        if ($activeTab) {
            this.setActiveTab($activeTab)

            const activePanelIds = $activeTab.getAttribute('aria-controls').split(',').filter(activePanelId => this.DOM.tabPanelIds.includes(activePanelId))

            this.DOM.$el.dispatchEvent(new CustomEvent('tabs:change', { detail: activePanelIds }), { bubbles: true })

            // only close open modals if it's a real popstate event
            if (typeof e.fromInit === 'undefined') {
                let $closeButton = document.querySelector('.js-close')
                if ($closeButton) {
                    $closeButton.dispatchEvent(new CustomEvent('close:close'), {
                        bubbles: true,
                        cancelable: true,
                    })
                }
            }
        } else {
            const $currentTab = document.getElementById(currentTabId)

            if (this.DOM.$el.contains($currentTab)) {
                $currentTab.dispatchEvent(new CustomEvent('element:should-be-visible', { bubbles: true }))
            }
        }
    }

    onElementShouldBeVisible(e) {
        let $tabPanel = e.target.closest('[role="tabpanel"]')

        if ($tabPanel && this.DOM.tabPanelIds.includes($tabPanel.id)) {
            let $tab = document.getElementById($tabPanel.getAttribute('aria-labelledby'))

            if ($tab) {
                this.setActiveTab($tab)
            }
        }
    }

    setActiveTab($activeTab) {
        const activePanelIds = $activeTab.getAttribute('aria-controls').split(',').filter(activePanelId => this.DOM.tabPanelIds.includes(activePanelId))
        const $activePanels = activePanelIds.map(activePanelId => document.getElementById(activePanelId))

        this.DOM.$tabs.filter($tab => $tab.id !== $activeTab.id).forEach($tab => $tab.setAttribute('aria-selected', 'false'))

        this.DOM.$tabPanels.filter($tabPanel => !activePanelIds.includes($tabPanel.id)).forEach($tabPanel => $tabPanel.setAttribute('hidden', 'hidden'))

        $activeTab.setAttribute('aria-selected', 'true')
        $activePanels.forEach($tabPanel => $tabPanel.removeAttribute('hidden'))
    }

    toggleHorizontallyScrollable() {
        if (this.DOM.$tabsList.scrollWidth > this.DOM.$tabsList.offsetWidth) {
            this.makeHorizontallyScrollable()
        } else {
            this.destroyHorizontallyScrollable()
        }
    }

    makeHorizontallyScrollable() {
        this.DOM.$el.classList.add('c-tabs--has-overflow')

        this.DOM.$tabsList.addEventListener('scroll', this.onHorizontalScroll.bind(this))
    }

    destroyHorizontallyScrollable() {
        this.DOM.$el.classList.remove('c-tabs--has-overflow')
        this.DOM.$el.classList.remove('c-tabs--has-scroll-left')
        this.DOM.$el.classList.remove('c-tabs--is-scroll-end')

        this.DOM.$tabsList.removeEventListener('scroll', this.onHorizontalScroll)

        this.DOM.$tabsList.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant',
        })
    }

    scrollHorizontally(e) {
        if (e.target.classList.contains('c-tabs__scroller--to-left')) {
            this.DOM.$tabsList.scrollTo({
                top: 0,
                left: this.DOM.$tabsList.scrollLeft - this.DOM.$tabsList.offsetWidth + e.target.offsetWidth * 2,
                behavior: 'smooth',
            })
        } else if (e.target.classList.contains('c-tabs__scroller--to-right')) {
            this.DOM.$tabsList.scrollTo({
                top: 0,
                left: this.DOM.$tabsList.scrollLeft + this.DOM.$tabsList.offsetWidth - e.target.offsetWidth * 2,
                behavior: 'smooth',
            })
        }
    }

    onHorizontalScroll() {
        if (this.DOM.$tabsList.scrollLeft > 0) {
            this.DOM.$el.classList.add('c-tabs--has-scroll-left')

            if (Math.ceil(this.DOM.$tabsList.scrollLeft) >= this.DOM.$tabsList.scrollWidth - this.DOM.$tabsList.offsetWidth) {
                this.DOM.$el.classList.add('c-tabs--is-scroll-end')
            } else {
                this.DOM.$el.classList.remove('c-tabs--is-scroll-end')
            }
        } else {
            this.DOM.$el.classList.remove('c-tabs--has-scroll-left')
            this.DOM.$el.classList.remove('c-tabs--is-scroll-end')
        }
    }

}

Tabs.prototype.defaults = {
    hashPrefix: 'tab-',
}
